import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, ProgressBar, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Quiz,
  WordConnectPlayStart,
  WordConnectCellDto,
  WordConnectLetterSlotDto,
  WordConnectPlayState,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  quizService,
  WordConnectFinishAudit,
  HostedOpenAsyncPvPSummary,
  pvpService,
} from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useTranslation } from 'react-i18next';
import { useQuizTimer } from '../hooks/useQuizTimer';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import SoloQuizQuitConfirmModal from './SoloQuizQuitConfirmModal';
import { isPwaStandalone } from '../utils/pwaStandalone';
import { playQuizSfxCorrect, playQuizSfxIncorrect, playTileMatchPickSfx } from '../audio/quizSfx';
import { wordConnectColorForIndex, wordConnectTint } from '../utils/wordConnectColors';
import { wordConnectWordLength } from '../utils/wordConnectWordLength';
import { normalizePlayDifficultyLabel } from '../utils/quizPlayBandScoring';
import { useWordConnectWasm } from '../hooks/useWordConnectWasm';
import {
  buildSoloArenaHostCompleteBody,
  type SoloArenaPublishSnapshot,
} from '../utils/soloArenaPublishLayout';


import './shared/QuizStyles.css';
import './WordConnectQuizPlayer.css';

const BOARD_LAYOUT_NARROW_MAX_PX = 520;
const BOARD_GAP_PX = 3;
const BOARD_PAD_PX = 8;
const BOARD_CELL_PX_MOBILE_MIN = 20;
const BOARD_CELL_PX_MOBILE_MAX = 36;
const BOARD_CELL_PX_DESKTOP_MIN = 30;
const BOARD_CELL_PX_DESKTOP_MAX = 44;
const LETTER_POOL_COLS = 8;
const LETTER_POOL_GAP_PX = 6;
const LETTER_POOL_WRAP_PAD_PX = 6;
const BOARD_LAYOUT_BOTTOM_MARGIN_PX = 12;
/** Extra lift above the finger so the board-sized tile stays visible while dragging. */
const TOUCH_DRAG_LIFT_EXTRA_PX = 32;

function wordConnectTouchDragLiftPx(cellPx: number): number {
  return cellPx + TOUCH_DRAG_LIFT_EXTRA_PX;
}

function clientToDragAnchor(
  clientX: number,
  clientY: number,
  isTouch: boolean,
  cellPx: number
): { x: number; y: number } {
  if (!isTouch) return { x: clientX, y: clientY };
  const lift = wordConnectTouchDragLiftPx(cellPx);
  return { x: clientX, y: clientY - lift };
}

function computeWordConnectCellPx(
  availW: number,
  availH: number,
  cols: number,
  rows: number,
  isNarrow: boolean
): number {
  const minCell = isNarrow ? BOARD_CELL_PX_MOBILE_MIN : BOARD_CELL_PX_DESKTOP_MIN;
  const maxCell = isNarrow ? BOARD_CELL_PX_MOBILE_MAX : BOARD_CELL_PX_DESKTOP_MAX;
  const pad = BOARD_PAD_PX * 2;
  const gapX = BOARD_GAP_PX * Math.max(0, cols - 1);
  const gapY = BOARD_GAP_PX * Math.max(0, rows - 1);
  const fitW = cols > 0 ? Math.floor((availW - pad - gapX) / cols) : minCell;
  const fitH = rows > 0 ? Math.floor((availH - pad - gapY) / rows) : minCell;
  return Math.min(maxCell, Math.max(minCell, Math.min(fitW, fitH)));
}

/** Analytical pool height — avoids ResizeObserver ↔ cell-size feedback loops. */
function computeLetterPoolHeightPx(cellPx: number, slotCount: number): number {
  if (slotCount <= 0) return LETTER_POOL_WRAP_PAD_PX;
  const poolRows = Math.ceil(slotCount / LETTER_POOL_COLS);
  return (
    LETTER_POOL_WRAP_PAD_PX +
    poolRows * cellPx +
    Math.max(0, poolRows - 1) * LETTER_POOL_GAP_PX
  );
}

function measureWordConnectBoardCellPx(
  availW: number,
  viewportH: number,
  boardTop: number,
  cols: number,
  rows: number,
  poolSlotCount: number,
  isNarrow: boolean
): number {
  const minCell = isNarrow ? BOARD_CELL_PX_MOBILE_MIN : BOARD_CELL_PX_DESKTOP_MIN;
  const poolH0 = computeLetterPoolHeightPx(minCell, poolSlotCount);
  let availH = Math.max(80, viewportH - boardTop - poolH0 - BOARD_LAYOUT_BOTTOM_MARGIN_PX);
  let cellPx = computeWordConnectCellPx(availW, availH, cols, rows, isNarrow);
  const poolH1 = computeLetterPoolHeightPx(cellPx, poolSlotCount);
  availH = Math.max(80, viewportH - boardTop - poolH1 - BOARD_LAYOUT_BOTTOM_MARGIN_PX);
  cellPx = computeWordConnectCellPx(availW, availH, cols, rows, isNarrow);
  return cellPx;
}

function difficultyBadgeVariant(band: 'Easy' | 'Medium' | 'Hard'): string {
  if (band === 'Hard') return 'danger';
  if (band === 'Easy') return 'success';
  return 'warning';
}

/** Server snapshot for save — avoids stale React state when the last letter completes the session. */
type WordConnectFinishSnapshot = Pick<
  WordConnectPlayState,
  'pointsPerWordSlot' | 'wordsCompleted' | 'totalScore'
>;

export interface WordConnectPvpFinishPayload {
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  duration: number;
  questionIdsInOrder: string[];
  responseTimeSecondsPerQuestion: number[];
  correctPerQuestion: boolean[];
  wordConnectFinishAudit?: WordConnectFinishAudit;
  wordConnectResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
}

export interface WordConnectQuizPlayerProps {
  quizId: string;
  quiz: Quiz;
  wordConnect: WordConnectPlayStart;
  startTime: Date;
  playDifficulty: string;
  onPlayAgain: () => void | Promise<void>;
  playAgainLoading?: boolean;
  embeddedInPvp?: boolean;
  onPvpQuitRequest?: () => void;
  pvpAsync?: {
    flow: 'host' | 'opponent';
    roomId: string;
    onComplete: (payload: WordConnectPvpFinishPayload) => Promise<void>;
  };
  arenaPublishSnapshotRef?: React.MutableRefObject<SoloArenaPublishSnapshot | null>;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishCheckedRef?: React.MutableRefObject<boolean>;
}

const WordConnectQuizPlayer: React.FC<WordConnectQuizPlayerProps> = ({
  quizId,
  quiz,
  wordConnect,
  startTime,
  playDifficulty,
  onPlayAgain,
  playAgainLoading = false,
  embeddedInPvp = false,
  onPvpQuitRequest,
  pvpAsync,
  arenaPublishSnapshotRef,
  arenaPublishChecked: arenaPublishCheckedProp,
  onArenaPublishCheckedChange: onArenaPublishCheckedChangeProp,
  arenaPublishCheckedRef: arenaPublishCheckedRefProp,
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [cells, setCells] = useState<WordConnectCellDto[]>(() =>
    wordConnect.cells.map((c) => ({ ...c }))
  );
  const [letterPool, setLetterPool] = useState<WordConnectLetterSlotDto[]>(() =>
    wordConnect.letterPool.map((s) => ({ ...s }))
  );
  const [words, setWords] = useState(() => wordConnect.words.map((w) => ({ ...w })));
  const [totalScore, setTotalScore] = useState(0);
  const [pointsPerWord, setPointsPerWord] = useState<number[]>(
    () => Array(wordConnect.activeWordCount).fill(0)
  );
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [dragSlotId, setDragSlotId] = useState<string | null>(null);
  const [pointerDragPos, setPointerDragPos] = useState<{
    x: number;
    y: number;
    isTouch: boolean;
  } | null>(null);
  const [returningSlotId, setReturningSlotId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ row: number; col: number } | null>(null);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const levelUpCelebration = useQuizFinishLevelUp();
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [resultDurationSeconds, setResultDurationSeconds] = useState<number | null>(null);
  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);

  const [fallbackArenaPublishChecked, setFallbackArenaPublishChecked] = useState(true);
  const fallbackArenaPublishCheckedRef = useRef(true);
  const arenaPublishChecked = arenaPublishCheckedProp ?? fallbackArenaPublishChecked;
  const setArenaPublishChecked = onArenaPublishCheckedChangeProp ?? setFallbackArenaPublishChecked;
  const arenaPublishCheckedRef = arenaPublishCheckedRefProp ?? fallbackArenaPublishCheckedRef;
  const [hostedOpenSummaryForArena, setHostedOpenSummaryForArena] =
    useState<HostedOpenAsyncPvPSummary | null>(null);
  const [arenaHostedSummaryLoading, setArenaHostedSummaryLoading] = useState(false);

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked, arenaPublishCheckedRef]);

  const arenaPublishOffer = useMemo(
    () =>
      isQuizCompleted &&
      !(quizResponse?.tooManySkips ?? false) &&
      wordConnect.activeWordCount > 0 &&
      wordsCompleted / wordConnect.activeWordCount >= 0.7,
    [isQuizCompleted, quizResponse?.tooManySkips, wordConnect.activeWordCount, wordsCompleted]
  );

  const arenaPublishBlockedOpenQuiz = useMemo(() => {
    const qid = quiz?.id?.trim();
    if (!qid || !hostedOpenSummaryForArena?.openQuizIds?.length) return false;
    const lower = qid.toLowerCase();
    return hostedOpenSummaryForArena.openQuizIds.some((id) => id.trim().toLowerCase() === lower);
  }, [quiz?.id, hostedOpenSummaryForArena]);

  const arenaPublishBlockedMaxRooms = useMemo(() => {
    if (!hostedOpenSummaryForArena) return false;
    return hostedOpenSummaryForArena.openCount >= hostedOpenSummaryForArena.maxOpenRooms;
  }, [hostedOpenSummaryForArena]);

  const arenaPublishBlocked = arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms;

  const arenaPublishBlockedReason = arenaPublishBlockedOpenQuiz
    ? ('duplicateQuiz' as const)
    : arenaPublishBlockedMaxRooms
      ? ('maxRooms' as const)
      : undefined;

  useEffect(() => {
    if (arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms) {
      setArenaPublishChecked(false);
    }
  }, [arenaPublishBlockedOpenQuiz, arenaPublishBlockedMaxRooms, setArenaPublishChecked]);

  useEffect(() => {
    if (!showResultsModal || !arenaPublishOffer || !isQuizCompleted || (quizResponse?.tooManySkips ?? false)) {
      setHostedOpenSummaryForArena(null);
      setArenaHostedSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setArenaHostedSummaryLoading(true);
    void pvpService
      .getHostedOpenAsyncSummary()
      .then((s) => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(s);
          setArenaHostedSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(null);
          setArenaHostedSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showResultsModal, arenaPublishOffer, isQuizCompleted, quizResponse?.tooManySkips]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = arenaPublishSnapshotRef?.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Medium';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      if (arenaPublishSnapshotRef) arenaPublishSnapshotRef.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t, arenaPublishSnapshotRef, arenaPublishCheckedRef]);

  const {
    ready: wasmReady,
    error: wasmError,
    tryPlaceLetter: wasmTryPlaceLetter,
    buildFinishPayload,
  } = useWordConnectWasm(wordConnect?.engineBootstrapCipher ?? undefined);

  const finishInFlightRef = useRef(false);
  const timeoutHandledRef = useRef(false);
  const questionIdsRef = useRef(
    wordConnect.activeQuestionIds?.length
      ? [...wordConnect.activeQuestionIds]
      : wordConnect.words.map((w) => w.questionId)
  );
  const pointsPerWordRef = useRef<number[]>(Array(wordConnect.activeWordCount).fill(0));
  const secondsPerWordRef = useRef<number[]>(Array(wordConnect.activeWordCount).fill(0));
  const wordRoundStartMsRef = useRef(Date.now());
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const activePointerDragRef = useRef<string | null>(null);
  const activePointerDragIsTouchRef = useRef(false);
  const dropTargetRef = useRef<{ row: number; col: number } | null>(null);
  const cellPxRef = useRef(36);

  useEffect(() => {
    questionIdsRef.current = wordConnect.activeQuestionIds?.length
      ? [...wordConnect.activeQuestionIds]
      : wordConnect.words.map((w) => w.questionId);
    pointsPerWordRef.current = Array(wordConnect.activeWordCount).fill(0);
    secondsPerWordRef.current = Array(wordConnect.activeWordCount).fill(0);
    wordRoundStartMsRef.current = Date.now();
  }, [wordConnect.playSessionId, wordConnect.activeWordCount, wordConnect.words]);

  const canDragLetters = wasmReady && !placing && !isQuizCompleted;

  const playBand = useMemo(() => normalizePlayDifficultyLabel(playDifficulty), [playDifficulty]);

  const handleCelebrationFinished = useCallback(() => {
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  }, [levelUpCelebration]);

  const handleLevelUpFinished = useCallback(() => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  }, [levelUpCelebration]);

  const gridBounds = useMemo(() => ({ minRow: 0, maxRow: 9, minCol: 0, maxCol: 9 }), []);

  const finishQuiz = useCallback(
    async (snapshot?: WordConnectFinishSnapshot) => {
      if (finishInFlightRef.current) return;
      finishInFlightRef.current = true;
      setSavingQuizAttempt(true);
      const finishedAt = new Date();
      setIsQuizCompleted(true);
      const duration = Math.max(0, Math.floor((finishedAt.getTime() - startTime.getTime()) / 1000));
      setResultDurationSeconds(duration);

      const questionIds = questionIdsRef.current;
      const slotPoints = snapshot?.pointsPerWordSlot ?? pointsPerWordRef.current;
      const witcResults = questionIds.map((_, i) => ({
        pointsEarned: slotPoints[i] ?? 0,
      }));
      const totalCorrect =
        snapshot?.wordsCompleted ?? witcResults.filter((r) => r.pointsEarned > 0).length;
      const score =
        snapshot?.totalScore ?? witcResults.reduce((s, r) => s + r.pointsEarned, 0);
      const allWordsDone = totalCorrect >= questionIds.length;
      const responseTimeSecondsPerQuestion = questionIds.map((_, i) =>
        Math.max(0, secondsPerWordRef.current[i] ?? 0)
      );

      setTotalScore(score);
      setWordsCompleted(totalCorrect);
      if (slotPoints.length) {
        setPointsPerWord([...slotPoints]);
        pointsPerWordRef.current = [...slotPoints];
      }

      const wordConnectFinishAudit = buildFinishPayload();
      if (!wordConnectFinishAudit) {
        finishInFlightRef.current = false;
        setIsQuizCompleted(false);
        setSaveAttemptError('Word Connect finish audit is missing. Reload the quiz and try again.');
        setSavingQuizAttempt(false);
        return;
      }

      try {
        if (pvpAsync) {
          await pvpAsync.onComplete({
            score,
            correctAnswers: totalCorrect,
            totalQuestions: questionIds.length,
            duration,
            questionIdsInOrder: questionIds,
            responseTimeSecondsPerQuestion,
            correctPerQuestion: questionIds.map((_, i) => (slotPoints[i] ?? 0) > 0),
            wordConnectFinishAudit,
            wordConnectResults: witcResults,
          });
          return;
        }

        const response = await quizService.saveQuizAttempt(
          {
            quizId,
            score,
            correctAnswers: totalCorrect,
            totalQuestions: questionIds.length,
            duration,
            // Submitted session (timeout or all words) — XP uses correctAnswers / witcResults, not 100% completion.
            isCompleted: true,
            answers: questionIds.map(() => 'wordconnect'),
            questionIdsInOrder: questionIds,
            responseTimeSecondsPerQuestion,
            witcResults,
            wordConnectFinishAudit,
            playDifficulty,
          },
          { pwaClient: isPwaStandalone() }
        );
        setQuizResponse(response);
        if (response.earnedXP !== undefined) setEarnedXP(response.earnedXP);

        if (arenaPublishSnapshotRef) {
          arenaPublishSnapshotRef.current = null;
          if (
            !response.tooManySkips &&
            questionIds.length > 0 &&
            totalCorrect / questionIds.length >= 0.7 &&
            quiz
          ) {
            arenaPublishSnapshotRef.current = {
              quizId,
              playDifficulty,
              roomDisplayName: (quiz.title || 'Arena').trim().slice(0, 80),
              score,
              correctAnswers: totalCorrect,
              totalQuestions: questionIds.length,
              durationSeconds: duration,
              skippedQuestions: 0,
              questionIdsInOrder: questionIds,
              answerIdsPerQuestion: questionIds.map(() => []),
              responseTimeSecondsPerQuestion,
              correctPerQuestion: questionIds.map((_, i) => (slotPoints[i] ?? 0) > 0),
              wordConnectFinishAudit,
              wordConnectResults: witcResults,
            };
          }
        }
        setArenaPublishChecked(true);

        const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
        const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
        const streakUpdate = response.loginStreakUpdated ?? null;
        const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
        const won = allWordsDone && !response.tooManySkips;
        if (won && (unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate)) {
          setCelebrationAchievements(unlocked);
          setCelebrationCharacters(unlockedChars);
          setCelebrationLoginStreak(streakUpdate);
          setShowCelebration(true);
        } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
          /* level-up modal */
        } else {
          setShowResultsModal(true);
        }
      } catch (error: unknown) {
        finishInFlightRef.current = false;
        setIsQuizCompleted(false);
        const msg =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String((error.response.data as { message?: string }).message)
            : error instanceof Error
              ? error.message
              : 'Unknown error';
        setSaveAttemptError(msg);
      } finally {
        setSavingQuizAttempt(false);
      }
    },
    [buildFinishPayload, quizId, playDifficulty, startTime, pvpAsync, arenaPublishSnapshotRef, quiz, levelUpCelebration]
  );

  const handleTimerExpired = useCallback(() => {
    if (timeoutHandledRef.current || finishInFlightRef.current) return;
    timeoutHandledRef.current = true;
    void finishQuiz();
  }, [finishQuiz]);

  const { timeLeft, stopTimer, addTime } = useQuizTimer({
    timeLimit: wordConnect.initialTimerSeconds,
    onTimeout: handleTimerExpired,
    isActive:
      !isQuizCompleted && !showResultsModal && wordsCompleted < wordConnect.activeWordCount,
  });

  const applyServerState = useCallback(
    (state: WordConnectPlayState) => {
      if (state.wordJustCompleted && state.pointsPerWordSlot?.length) {
        const prev = pointsPerWordRef.current;
        const elapsed = Math.max(1, Math.floor((Date.now() - wordRoundStartMsRef.current) / 1000));
        for (let i = 0; i < state.pointsPerWordSlot.length; i++) {
          if ((state.pointsPerWordSlot[i] ?? 0) > 0 && (prev[i] ?? 0) === 0) {
            secondsPerWordRef.current[i] = elapsed;
            wordRoundStartMsRef.current = Date.now();
            break;
          }
        }
      }

      if (state.pointsPerWordSlot?.length) {
        pointsPerWordRef.current = [...state.pointsPerWordSlot];
        setPointsPerWord(state.pointsPerWordSlot);
      }

      setCells(state.cells.map((c) => ({ ...c })));
      setLetterPool(state.letterPool.map((s) => ({ ...s })));
      setWords((prev) =>
        state.words.map((w) => {
          const prior = prev.find((p) => p.questionId === w.questionId);
          const length = w.length > 0 ? w.length : prior?.length ?? wordConnectWordLength(w);
          return {
            questionId: w.questionId,
            text: '',
            length,
            startRow: w.startRow,
            startCol: w.startCol,
            isHorizontal: w.isHorizontal,
            isCompleted: w.isCompleted,
          };
        })
      );
      setTotalScore(state.totalScore);
      setWordsCompleted(state.wordsCompleted);
      addTime(wordConnect.timeBonusPerLetterSeconds);
      if (state.wordJustCompleted) {
        addTime(wordConnect.timeBonusPerWordSeconds);
      }
      if (state.sessionComplete) {
        stopTimer();
        const snapshot = {
          pointsPerWordSlot: state.pointsPerWordSlot ?? pointsPerWordRef.current,
          wordsCompleted: state.wordsCompleted,
          totalScore: state.totalScore,
        };
        // Let WASM finish mutating session state before building the signed audit payload.
        queueMicrotask(() => {
          void finishQuiz(snapshot);
        });
      }
    },
    [addTime, finishQuiz, stopTimer, wordConnect.timeBonusPerLetterSeconds, wordConnect.timeBonusPerWordSeconds]
  );

  const handleRejectedPlacement = useCallback((slotId: string) => {
    playQuizSfxIncorrect();
    setReturningSlotId(slotId);
    window.setTimeout(() => setReturningSlotId(null), 400);
  }, []);

  const placeLetter = useCallback(
    async (slotId: string, row: number, col: number) => {
      if (placing || isQuizCompleted || !wasmReady) return;
      setPlacing(true);
      try {
        const state = await wasmTryPlaceLetter(slotId, row, col);
        if (state.placementAccepted === false) {
          handleRejectedPlacement(slotId);
          return;
        }
        if (state.wordJustCompleted) {
          playQuizSfxCorrect();
        } else {
          playTileMatchPickSfx();
        }
        applyServerState(state);
      } catch (error: unknown) {
        handleRejectedPlacement(slotId);
        const msg = error instanceof Error ? error.message : null;
        if (msg) {
          toast.error(msg);
        }
      } finally {
        setPlacing(false);
        dropTargetRef.current = null;
        setDropTarget(null);
        setDragSlotId(null);
        setPointerDragPos(null);
      }
    },
    [applyServerState, handleRejectedPlacement, isQuizCompleted, placing, wasmReady, wasmTryPlaceLetter]
  );

  const cellKey = (row: number, col: number) => `${row}:${col}`;

  const cellMap = useMemo(() => {
    const m = new Map<string, WordConnectCellDto>();
    for (const c of cells) m.set(cellKey(c.row, c.col), c);
    return m;
  }, [cells]);

  const resolveDropCellFromPoint = useCallback(
    (clientX: number, clientY: number): { row: number; col: number } | null => {
      const el = document.elementFromPoint(clientX, clientY);
      const cellEl = el?.closest('[data-wc-row][data-wc-col]') as HTMLElement | null;
      if (!cellEl) return null;
      const row = Number(cellEl.dataset.wcRow);
      const col = Number(cellEl.dataset.wcCol);
      if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
      const cell = cellMap.get(cellKey(row, col));
      if (!cell || cell.letter) return null;
      return { row, col };
    },
    [cellMap]
  );

  const clearDragVisual = useCallback(() => {
    activePointerDragRef.current = null;
    activePointerDragIsTouchRef.current = false;
    dropTargetRef.current = null;
    setDragSlotId(null);
    setDropTarget(null);
    setPointerDragPos(null);
  }, []);

  const finishPointerDrag = useCallback(
    (slotId: string, clientX: number, clientY: number, isTouch: boolean) => {
      if (activePointerDragRef.current !== slotId) return;
      const anchor = clientToDragAnchor(clientX, clientY, isTouch, cellPxRef.current);
      const target =
        dropTargetRef.current ?? resolveDropCellFromPoint(anchor.x, anchor.y);
      clearDragVisual();
      if (target) {
        void placeLetter(slotId, target.row, target.col);
      }
    },
    [clearDragVisual, placeLetter, resolveDropCellFromPoint]
  );

  const handleLetterPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, slotId: string) => {
      if (!canDragLetters || e.button !== 0) return;
      e.preventDefault();
      const isTouch = e.pointerType === 'touch';
      activePointerDragIsTouchRef.current = isTouch;
      activePointerDragRef.current = slotId;
      const rect = e.currentTarget.getBoundingClientRect();
      cellPxRef.current = rect.width;
      setDragSlotId(slotId);
      setPointerDragPos({
        ...clientToDragAnchor(e.clientX, e.clientY, isTouch, rect.width),
        isTouch,
      });
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [canDragLetters]
  );

  const handleLetterPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, slotId: string) => {
      if (activePointerDragRef.current !== slotId) return;
      const isTouch = activePointerDragIsTouchRef.current;
      const anchor = clientToDragAnchor(e.clientX, e.clientY, isTouch, cellPxRef.current);
      setPointerDragPos({ ...anchor, isTouch });
      const target = resolveDropCellFromPoint(anchor.x, anchor.y);
      dropTargetRef.current = target;
      setDropTarget(target);
    },
    [resolveDropCellFromPoint]
  );

  const handleLetterPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, slotId: string) => {
      if (activePointerDragRef.current !== slotId) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishPointerDrag(
        slotId,
        e.clientX,
        e.clientY,
        activePointerDragIsTouchRef.current
      );
    },
    [finishPointerDrag]
  );

  const handleLetterPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, slotId: string) => {
      if (activePointerDragRef.current !== slotId) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      clearDragVisual();
    },
    [clearDragVisual]
  );

  const clueByCell = useMemo(() => {
    const m = new Map<string, number>();
    words.forEach((w, index) => {
      m.set(cellKey(w.startRow, w.startCol), index + 1);
    });
    return m;
  }, [words]);

  /** Same index as clue number minus 1 — each word paints its whole path (later words win at crosses). */
  const cellColorIndexByKey = useMemo(() => {
    const m = new Map<string, number>();
    words.forEach((w, wordIdx) => {
      const dr = w.isHorizontal ? 0 : 1;
      const dc = w.isHorizontal ? 1 : 0;
      for (let i = 0; i < wordConnectWordLength(w); i++) {
        const k = cellKey(w.startRow + dr * i, w.startCol + dc * i);
        m.set(k, wordIdx);
      }
    });
    return m;
  }, [words]);

  const colorIndexByQuestionId = useMemo(() => {
    const m = new Map<string, number>();
    words.forEach((w, idx) => m.set(w.questionId, idx));
    return m;
  }, [words]);

  const draggingSlotVisual = useMemo(() => {
    if (!dragSlotId) return null;
    const slot = letterPool.find((s) => s.slotId === dragSlotId);
    if (!slot) return null;
    const colorIdx =
      slot.wordColorIndex ??
      (slot.sourceQuestionId
        ? colorIndexByQuestionId.get(slot.sourceQuestionId)
        : undefined) ??
      0;
    const accent = wordConnectColorForIndex(colorIdx);
    return {
      letter: slot.letter,
      isTouchDrag: pointerDragPos?.isTouch === true,
      style: {
        width: cellPxRef.current,
        height: cellPxRef.current,
        background: `linear-gradient(145deg, ${accent}, ${accent}dd)`,
        borderColor: accent,
      } as React.CSSProperties,
    };
  }, [colorIndexByQuestionId, dragSlotId, letterPool, pointerDragPos?.isTouch]);

  const activeWordCellKeys = useMemo(() => {
    if (!dragSlotId) return new Set<string>();

    const slot = letterPool.find((s) => s.slotId === dragSlotId);
    const draggedWord =
      slot?.sourceQuestionId != null
        ? words.find((w) => w.questionId === slot.sourceQuestionId)
        : undefined;

    const highlightWord =
      draggedWord ??
      (dropTarget
        ? words.find((w) => {
            const dr = w.isHorizontal ? 0 : 1;
            const dc = w.isHorizontal ? 1 : 0;
            const targetKey = cellKey(dropTarget.row, dropTarget.col);
            for (let i = 0; i < wordConnectWordLength(w); i++) {
              if (cellKey(w.startRow + dr * i, w.startCol + dc * i) === targetKey) return true;
            }
            return false;
          })
        : undefined);

    if (!highlightWord) return new Set<string>();

    const keys = new Set<string>();
    const dr = highlightWord.isHorizontal ? 0 : 1;
    const dc = highlightWord.isHorizontal ? 1 : 0;
    for (let i = 0; i < wordConnectWordLength(highlightWord); i++) {
      keys.add(cellKey(highlightWord.startRow + dr * i, highlightWord.startCol + dc * i));
    }
    return keys;
  }, [dragSlotId, dropTarget, letterPool, words]);

  const cellAccentStyle = (
    colorIdx: number | undefined,
    variant: 'empty' | 'filled' | 'active' | 'target'
  ): React.CSSProperties | undefined => {
    if (colorIdx === undefined) return undefined;
    const accent = wordConnectColorForIndex(colorIdx);
    if (variant === 'target') {
      return {
        backgroundColor: '#ffe066',
        borderColor: '#f0c419',
        borderWidth: 2,
        color: '#1a1a1a',
      };
    }
    if (variant === 'active') {
      return {
        backgroundColor: wordConnectTint(colorIdx, 0.32),
        borderColor: accent,
        color: '#1a1a1a',
      };
    }
    if (variant === 'filled') {
      return {
        backgroundColor: wordConnectTint(colorIdx, 0.18),
        borderColor: accent,
        color: accent,
      };
    }
    return {
      backgroundColor: wordConnectTint(colorIdx, 0.1),
      borderColor: accent,
      color: '#1a1a1a',
    };
  };

  const rows = 10;
  const cols = 10;

  const progressPercent = useMemo(() => {
    const total = wordConnect.activeWordCount;
    if (total <= 0) return 0;
    // Same as other solo modes: first word = 10% ((index + 1) / total).
    return Math.min(100, ((wordsCompleted + 1) / total) * 100);
  }, [wordsCompleted, wordConnect.activeWordCount]);

  const currentWordLabel = useMemo(() => {
    const total = wordConnect.activeWordCount;
    const current =
      total > 0 ? Math.min(wordsCompleted + 1, total) : 0;
    return t('wordConnect.wordProgress', 'Palavra {{current}} de {{total}}')
      .replace('{{current}}', String(current))
      .replace('{{total}}', String(total));
  }, [t, wordConnect.activeWordCount, wordsCompleted]);

  // Removed dynamic board measurement to prevent resize loop jitters and enable responsive CSS layout.

  const handleQuitQuiz = () => {
    if (onPvpQuitRequest) {
      onPvpQuitRequest();
    } else {
      setShowQuitModal(true);
    }
  };

  const confirmQuit = useCallback(() => {
    stopTimer();
    setShowQuitModal(false);
    setResultDurationSeconds(
      Math.max(0, Math.floor((Date.now() - startTime.getTime()) / 1000))
    );
    setIsQuizCompleted(false);
    setShowResultsModal(true);
  }, [startTime, stopTimer]);

  if (wasmError) {
    const errBody = (
      <div className={embeddedInPvp ? 'word-connect-quiz-player-embedded word-connect-quiz-player' : 'quiz-container'}>
        <Alert variant="danger">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{wasmError}</p>
        </Alert>
      </div>
    );
    if (embeddedInPvp) return errBody;
    return (
      <div className="quiz-page word-connect-quiz-player">
        {errBody}
      </div>
    );
  }

  if (!wasmReady && !showResultsModal && !showCelebration && !levelUpCelebration.showLevelUp) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  if (saveAttemptError) {
    const errBody = (
      <div className={embeddedInPvp ? 'word-connect-quiz-player-embedded word-connect-quiz-player' : 'quiz-container'}>
        <div className="quiz-error">
          <Alert variant="danger">
            <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
            <p>{saveAttemptError}</p>
            <Button variant="outline-danger" onClick={() => navigate('/', { replace: true })}>
              {t('quiz.backToHome', 'Back to Home')}
            </Button>
          </Alert>
        </div>
      </div>
    );
    if (embeddedInPvp) return errBody;
    return (
      <div className="quiz-page word-connect-quiz-player">
        {errBody}
      </div>
    );
  }

  const wordConnectPlayBody = (
    <>
      <div className={embeddedInPvp ? 'word-connect-quiz-player-embedded word-connect-quiz-player' : 'quiz-container'}>
        <div className="quiz-header quiz-page-header">
          <QuizPlayPageHeading quiz={quiz} language={i18n.language} />
          <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
              <Badge bg={difficultyBadgeVariant(playBand)}>
                {t(`quiz.${playBand.toLowerCase()}`, playBand)}
              </Badge>
              <span className="text-muted quiz-page-header__question-of">
                {currentWordLabel}
              </span>
              {!isQuizCompleted ? (
                <Badge
                  bg={timeLeft <= 10 ? 'danger' : timeLeft <= 20 ? 'warning' : 'info'}
                  className="timer-badge"
                >
                  {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
                </Badge>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <NavbarMusicToggle className="flex-shrink-0" />
              <QuizSoundToggleButton disabled={savingQuizAttempt || placing} />
              <Button
                type="button"
                variant="outline-danger"
                onClick={handleQuitQuiz}
                className="quiz-quit-icon-btn flex-shrink-0"
                aria-label={t('quiz.quitQuiz', 'Quit Quiz')}
                title={t('quiz.quitQuiz', 'Quit Quiz')}
                disabled={savingQuizAttempt || showResultsModal || showQuitModal}
              >
                <i className="bi bi-x-lg" aria-hidden />
              </Button>
            </div>
          </div>
        </div>

        <ProgressBar
          now={progressPercent}
          className="quiz-progress-bar"
          label={`${Math.round(progressPercent)}%`}
        />

        <Card className="question-card quiz-compact mb-3">
          <Card.Body>
            <Card.Title className="question-title h5 mb-2">
              {t('wordConnect.instruction', 'Arraste as letras para completar as palavras cruzadas')}
            </Card.Title>
            <p className="small mb-3 fw-medium text-success">
              {t('genius.roundPoints', '+{{pts}} pontos nesta rodada').replace(
                '{{pts}}',
                String(wordConnect.pointsPerWord)
              )}
            </p>

            <div className="word-connect-play-area">
              <div ref={boardAreaRef} className="word-connect-board-wrap">
                <div className="word-connect-board">
                  {Array.from({ length: rows }, (_, ri) =>
                    Array.from({ length: cols }, (_, ci) => {
                      const row = gridBounds.minRow + ri;
                      const col = gridBounds.minCol + ci;
                      const cell = cellMap.get(cellKey(row, col));
                      if (!cell) {
                        return (
                          <div
                            key={`${row}-${col}`}
                            className="word-connect-cell word-connect-cell--blocked"
                            aria-hidden
                          />
                        );
                      }
                      const filled = Boolean(cell.letter);
                      const key = cellKey(row, col);
                      const isTarget =
                        dropTarget?.row === row && dropTarget?.col === col;
                      const isWordActive =
                        dragSlotId != null && activeWordCellKeys.has(key);
                      const clue = clueByCell.get(key);
                      const colorIdx =
                        filled && cell.wordColorIndex != null
                          ? cell.wordColorIndex
                          : cellColorIndexByKey.get(key);
                      const cellVariant = isTarget
                        ? 'target'
                        : isWordActive
                          ? 'active'
                          : filled
                            ? 'filled'
                            : 'empty';
                      return (
                        <div
                          key={`${row}-${col}`}
                          className={[
                            'word-connect-cell',
                            filled ? 'word-connect-cell--filled' : 'word-connect-cell--empty',
                            isTarget ? 'word-connect-cell--drop-target' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={cellAccentStyle(colorIdx, cellVariant)}
                          data-wc-row={row}
                          data-wc-col={col}
                        >
                          {clue != null ? (
                            <span className="word-connect-cell-clue" aria-hidden>
                              {clue}
                            </span>
                          ) : null}
                          {cell.letter ?? ''}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="word-connect-letter-pool-wrap">
                <div className="word-connect-letter-pool">
                  {letterPool.map((slot) => {
                    const colorIdx =
                      slot.wordColorIndex ??
                      (slot.sourceQuestionId
                        ? colorIndexByQuestionId.get(slot.sourceQuestionId)
                        : undefined) ??
                      0;
                    const accent = wordConnectColorForIndex(colorIdx);
                    return (
                      <div
                        key={slot.slotId}
                        className={[
                          'word-connect-letter-slot',
                          dragSlotId === slot.slotId ? 'word-connect-letter-slot--dragging' : '',
                          returningSlotId === slot.slotId ? 'word-connect-letter-slot--returning' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{
                          background: `linear-gradient(145deg, ${accent}, ${accent}dd)`,
                          borderColor: accent,
                        }}
                        onPointerDown={(e) => handleLetterPointerDown(e, slot.slotId)}
                        onPointerMove={(e) => handleLetterPointerMove(e, slot.slotId)}
                        onPointerUp={(e) => handleLetterPointerUp(e, slot.slotId)}
                        onPointerCancel={(e) => handleLetterPointerCancel(e, slot.slotId)}
                      >
                        {slot.letter}
                      </div>
                    );
                  })}
                </div>
                {placing ? (
                  <div className="word-connect-letter-pool-loading">
                    <Spinner size="sm" animation="border" role="status" />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="quiz-stats-actions-container">
              <div className="quiz-stats-inline">
                <div className="stat-line">
                  {t('quiz.score', 'Pontuação')}: {totalScore}
                </div>
                <div className="stat-line">
                  {t('quiz.correctAnswers', 'Respostas Corretas')}: {wordsCompleted}/
                  {wordConnect.activeWordCount}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {savingQuizAttempt ? (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">
              {t('quiz.savingResults', 'Saving results...')}
            </span>
          </div>
        </div>
      ) : null}

      {pointerDragPos && draggingSlotVisual ? (
        <div
          className={[
            'word-connect-drag-ghost',
            draggingSlotVisual.isTouchDrag ? 'word-connect-drag-ghost--touch' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            left: pointerDragPos.x,
            top: pointerDragPos.y,
            ...draggingSlotVisual.style,
          }}
          aria-hidden
        >
          {draggingSlotVisual.letter}
        </div>
      ) : null}

      {!embeddedInPvp && (
        <SoloQuizQuitConfirmModal
          show={showQuitModal}
          onHide={() => setShowQuitModal(false)}
          onConfirm={confirmQuit}
        />
      )}

      {!embeddedInPvp && (
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handleCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
        />
      )}

      {!embeddedInPvp && (
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handleLevelUpFinished}
        />
      )}

      {!embeddedInPvp && (
        <SoloQuizResultsModal
          show={showResultsModal}
          hasTooManySkips={quizResponse?.tooManySkips ?? false}
          isQuizCompleted={isQuizCompleted}
          finishedAsGameOver={isQuizCompleted && wordsCompleted < wordConnect.activeWordCount}
          quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
          correctAnswers={quizResponse?.correctAnswers ?? wordsCompleted}
          totalQuestions={wordConnect.activeWordCount}
          score={totalScore}
          earnedXP={quizResponse?.earnedXP ?? earnedXP}
          quizResponse={quizResponse}
          resultDurationSeconds={resultDurationSeconds}
          onPlayAgain={onPlayAgain}
          onBackToHome={() => {
            void executeArenaPublishFromSoloIfNeeded().then(() => {
              setShowResultsModal(false);
              setShowCelebration(false);
              navigate('/', { replace: true });
            });
          }}
          playAgainLoading={playAgainLoading}
          arenaPublishOffer={arenaPublishOffer}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishAvailabilityLoading={arenaHostedSummaryLoading}
          arenaPublishBlocked={arenaPublishBlocked}
          arenaPublishBlockedReason={arenaPublishBlockedReason}
          arenaPublishMaxOpenRooms={hostedOpenSummaryForArena?.maxOpenRooms}
        />
      )}
    </>
  );

  if (embeddedInPvp) {
    return wordConnectPlayBody;
  }

  return (
    <div className="quiz-page word-connect-quiz-player">
      {wordConnectPlayBody}
    </div>
  );
};

export default WordConnectQuizPlayer;
